<template>
	<section class="learner_profile_resume portfolio-section" :aria-label="$t('learnerProfileResumePage')">

		<div class="hidden-container">
	    <form method="POST" id="resumeUploadForm" enctype="multipart/form-data" v-on:submit.prevent>
	      <input type="file" id="resumeFile" name="file" ref="fileInput" @change="fileToBeUploaded" />
	    </form>
	  </div>

		<div class="portfolio-section__head">
			<h2 class="portfolio-section__title">{{ $t('portfolio') }}</h2>
			<div class="portfolio-section__header-actions">
				<button
					type="button"
					class="portfolio-header-btn portfolio-header-btn--outline"
					:disabled="isUploading"
					@click="chooseFile">
					{{ $t('uploadResume') }}
				</button>
				<button
					type="button"
					class="portfolio-header-btn portfolio-header-btn--primary"
					@click="openResumeBuilder">
					{{ $t('startResumeBuilder') }}
				</button>
			</div>
		</div>

		<div class="portfolio-section__body">
			<div class="portfolio-upload-status" v-if="isUploading">
				{{ $t('upload') }}...
			</div>

			<template v-else-if="hasResumeVersions">
				<p class="resume-list__hint" v-if="resumeVersions.length > 1">
					{{ $t('makePrimaryByRadio') }}
				</p>
				<ul class="resume-list">
					<li
						class="resume-list__item"
						v-for="(version, index) in resumeVersions"
						:key="resumeVersionKey(version, index)">
						<div class="resume-list__row">
							<input
								type="radio"
								class="resume-list__radio"
								:id="'resume-' + index"
								:value="version.id"
								v-model="inlinePrimaryResumeId"
								@change="onInlinePrimaryChange">
							<span class="resume-list__icon ic ic-file-text" aria-hidden="true"></span>
							<div class="resume-list__details">
								<label class="resume-list__filename" :for="'resume-' + index">
									{{ version.fileName }}
								</label>
								<div class="resume-list__meta">
									<span>{{ version.uploadedBy }}</span>
									<span>{{ changeMillisToDate(version.uploadedAt) }}</span>
								</div>
							</div>
							<div class="resume-list__actions">
								<span class="resume-list__badge" v-if="version.primary">{{ $t('primary') }}</span>
								<button
									type="button"
									class="resume-list__action"
									:aria-label="$t('download')"
									@click="download(version)">
									<span class="fa fa-download" aria-hidden="true"></span>
								</button>
								<button
									type="button"
									class="resume-list__action"
									:aria-label="$t('confirmDeleteResume')"
									@click="showDeleteProfileResume(version)">
									<span class="fa fa-trash" aria-hidden="true"></span>
								</button>
							</div>
						</div>
					</li>
				</ul>
			</template>

			<p class="resume-list__empty" v-else>{{ $t('noResumeUploadedMsg') }}</p>
		</div>

		<div>
      <b-modal ref="deleteProfileResumeModal" :header-bg-variant="'profile-popup-header'" :title="$t('pleaseConfirm')" hide-header-close no-close-on-backdrop no-close-on-esc centered>
        <div class="text-center">
          <h6>{{ $t('confirmDeleteResume') }}</h6>
        </div>
        <div slot="modal-footer" class="w-100">
          <b-button @click="deleteProfileResume" class="btn button-primary float-right mr-2">{{ $t('yes') }}</b-button>
          <b-link class="float-right mr-4 mt-1" @click="cancelDeleteProfileResume">{{ $t('noDoNotDelete') }}</b-link>
        </div>
      </b-modal>
    </div>

	</section>
</template>

<script>
	import _ from 'lodash'
	import { DateTime } from 'luxon'
	import { AwsObject } from '../../../aws'
	import { mapState, mapActions } from 'vuex'

  var isValidFile = function (file) {
    let fileName = file.name
    let pdfRegEx = /(.pdf)$/i
    let docRegEx = /(.doc)$/i
    let docxRegEx = /(.docx)$/i
    return pdfRegEx.test(fileName) || docRegEx.test(fileName) || docxRegEx.test(fileName)
  }

  var getFileName = function (fileName) {
	  if (!fileName && fileName !== null) {
	    return null
	  }
	  return fileName.replace(/\.\w+$/g, '')
	}

  var getUpdatedFileName = function (title, fileName) {
	  var ext = fileName.substr((~-fileName.lastIndexOf('.') >>> 0) + 2)
	  if (ext !== '' || ext !== null) {
	    return (title + '.' + ext)
	  } else {
	    return fileName
	  }
	}

	var FileObject = function (title, file, size, timestamp, type) {
	  this.title = getFileName(title)
	  this.file = file
	  this.fileName = getUpdatedFileName(this.title, title)
	  this.size = size
	  this.contentFileType = type
	  this.timestamp = timestamp
	}

	export default {
		name: 'learner-profile-resume',
		data: () => {
			return {
				file: undefined,
				isUploading: false,
				inlinePrimaryResumeId: undefined,
				selectedToDelete: undefined
			}
		},
		computed: {
			...mapState({
				'userObject': state => state.login.userObj,
        'currentEnvironment': state => state.dashboard.currentEnvironment
      }),
      uuid () {
				return this.userObject.uuid
			},
			resume () {
      	if (this.userObject && this.userObject.userProfile) {
      		return this.userObject.userProfile.resume
      	}
      	return null
      },
      resumeVersions () {
      	if (!this.resume || !this.resume.uploadedVersions) {
      		return []
      	}
      	return this.resume.uploadedVersions
      },
      hasResumeVersions () {
      	return this.resumeVersions.length > 0
      },
      primaryResume () {
      	return _.find(this.resumeVersions, (resume) => resume.primary) || null
      }
		},
		watch: {
			primaryResume: {
				immediate: true,
				handler (resume) {
					if (resume) {
						this.inlinePrimaryResumeId = resume.id
					}
				}
			}
		},
		mounted () {
      this.loadCurrentEnvironment().then(res => {
        AwsObject(res)
      })
    },
    methods: {
			...mapActions([
				'loadUserObj',
        'loadCurrentEnvironment',
        'loadUpdateProfileResume',
        'loadDeleteProfileResume',
        'loadUpdateProfileResumeAsPrimary',
				'getAIServerConnectionInfo'
	    ]),
	    resumeVersionKey (version, index) {
	    	const id = version && version.id != null ? String(version.id) : ''
	    	const file = version && version.fileName ? version.fileName : ''
	    	return `resume-${index}-${id}-${file}`
	    },
	    showLoader () {
	      this.isUploading = true
	    },
	    hideLoader () {
	      this.isUploading = false
	    },
	    syncPrimaryResumeId () {
	    	if (this.primaryResume) {
	    		this.inlinePrimaryResumeId = this.primaryResume.id
	    	}
	    },
			onInlinePrimaryChange () {
				if (!this.inlinePrimaryResumeId) {
					return
				}
				this.loadUpdateProfileResumeAsPrimary({ uuid: this.uuid, id: this.inlinePrimaryResumeId }).then(userProfile => {
					this.userObject.userProfile = userProfile
					this.syncPrimaryResumeId()
				})
			},
			async openResumeBuilder() {
				const connectionInfo = await this.getAIServerConnectionInfo();
				const base = String(connectionInfo.uri || "").replace(/\/+$/, "");
				const token = connectionInfo.token;

				if (!base || !token) {
					console.warn("Missing Pragya URI or token");
					return;
				}

				const ssoUrl = `${base}/#/sso/consume?token=${encodeURIComponent(
					token
				)}&resource=resume`;

				const child = window.open(ssoUrl, "_blank", "noopener,noreferrer");
				if (!child) {
					return;
				}
			},
			clearFilePath () {
	      const input = this.$refs.fileInput
        input.type = 'text'
        input.type = 'file'
	    },
			chooseFile () {
				let sel = 'id=resumeFile'
        $('input[type=file][' + sel + ']').click()
			},
			fileToBeUploaded (ev) {
        let files = ev.target.files || ev.dataTransfer.files
        if (!files.length) {
          return
        }
        this.file = files[0]
        if (isValidFile(this.file)) {
        	let size = this.file.size
        	if (Math.abs(size/5242880) <= 1) {
        		this.showLoader()
	        	let timestamp = new Date().getTime()
	          let fileData = new FileObject(this.file.name, this.file, size, timestamp, this.file.type)
	          let id = this.uuid + '-' + timestamp
	          let key = id + '-' + fileData.fileName
	          this.storeFilesToS3({id: id, key: key, fileData: fileData})
        	} else {
        		this.hideLoader()
        		this.clearFilePath()
        		this.$notify({
	            group: 'notify',
	            type: 'error',
	            title: '',
	            text: this.$t('resumeFileSizeLimitError')
	          })
        	}
        } else {
          this.clearFilePath()
          this.$notify({
            group: 'notify',
            type: 'error',
            title: '',
            text: this.$t('resumeValidFileTypeError')
          })
        }
      },
      storeFilesToS3 (item) {
      	let s3AwsRef = new AWS.S3()
        let s3Bucket = this.currentEnvironment.s3Bucket
        let fileName = item.fileData.fileName
        var params = {
          Key: item.key,
          Bucket: s3Bucket,
          ContentType: item.fileData.contentFileType,
          ACL: 'public-read',
          Body: item.fileData.file,
          ContentDisposition: 'attachment; filename="' + fileName + '"'
        }
        s3AwsRef.putObject(params, (err, data) => {
          if (err) {
          	this.clearFilePath()
            this.hideLoader()
            this.$notify({
              group: 'notify',
              type: 'error',
              title: '',
              text: this.$t('resumeUploadFailed', { fileName })
            })
          } else {
          	let uploadedVersions = []
	          let fileUrl = 'https://'.concat(s3Bucket).concat('.s3-us-west-2.amazonaws.com/').concat(item.key).concat('?versionId=' + data.VersionId)
	          if (this.resume) {
	          	uploadedVersions = this.resume.uploadedVersions
	          }
	          let resume = {
	          	latestVersion: {
	          		id: item.id,
	          		key: item.key,
	          		primary: true,
	          		fileUrl: fileUrl,
	          		fileName: fileName
	          	},
	          	uploadedVersions: uploadedVersions
	          }
	          this.loadUpdateProfileResume({uuid: this.uuid, resume: resume}).then(userProfile => {
	          	this.userObject.userProfile = userProfile
	          	this.syncPrimaryResumeId()
	          	this.hideLoader()
	          	this.clearFilePath()
	          	this.$notify({
		            group: 'notify',
		            type: 'success',
		            title: '',
		            text: this.$t('resumeUploadSuccess', { fileName })
		          })
	          })
          }
        })
      },
      download (currentVersion) {
      	let path = currentVersion.fileUrl
      	let filename = currentVersion.fileName
      	const anchor = document.createElement('a')
		    anchor.href = path
		    anchor.download = filename
		    document.body.appendChild(anchor)
		    anchor.click()
		    document.body.removeChild(anchor)
      },
      showDeleteProfileResume (selected) {
      	this.selectedToDelete = selected
      	this.$refs.deleteProfileResumeModal.doShow()
      },
      deleteProfileResume () {
      	let s3AwsRef = new AWS.S3()
        let s3Bucket = this.currentEnvironment.s3Bucket
        let id = this.selectedToDelete.id
        let key = this.selectedToDelete.key
        let fileName = this.selectedToDelete.fileName
        var params = {
          Key: key,
          Bucket: s3Bucket
        }
        this.cancelDeleteProfileResume()
        this.showLoader()
        s3AwsRef.deleteObject(params, (err, data) => {
				  if (err) {
				  	this.hideLoader()
            this.$notify({
              group: 'notify',
              type: 'error',
              title: '',
              text: this.$t('resumeDeleteFailed', { fileName })
            })
          } else {
          	this.loadDeleteProfileResume({uuid: this.uuid, id: id}).then(userProfile => {
		      		this.userObject.userProfile = userProfile
		      		this.syncPrimaryResumeId()
		      		this.hideLoader()
		      		this.$notify({
		            group: 'notify',
		            type: 'success',
		            title: '',
		            text: this.$t('resumeDeleteSuccess', { fileName })
		          })
		        })
          }
				})
      },
      cancelDeleteProfileResume () {
      	this.selectedToDelete = undefined
      	this.$refs.deleteProfileResumeModal.hide()
      },
			changeMillisToDate (millis) {
        return DateTime.fromMillis(millis).toFormat('h:mm a, MMM dd, yyyy')
      }
	  }
	}
</script>

<style lang="scss" scoped>
$profile-green: var(--TU__BtnClrPri, #009f51);
$profile-green-hover: var(--TU__BtnHoverClrPri, #007a3f);
$profile-card-bg: #fff;
$profile-text: #303030;
$profile-text-muted: #6e6e6e;
$profile-border: #e5e5e5;
$profile-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
$profile-radius: 10px;

.portfolio-section {
	display: block;
	width: 100%;
	height: auto;
	max-height: none;
	overflow: visible;
	background: $profile-card-bg;
	border-radius: $profile-radius;
	box-shadow: $profile-shadow;
	margin-bottom: 16px;
}

.portfolio-section__head {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	padding: 14px 16px;
	border-bottom: 1px solid $profile-border;
}

.portfolio-section__title {
	margin: 0;
	font-size: 1rem;
	font-weight: 600;
	color: $profile-text;
	text-transform: capitalize;
	flex: 1 1 auto;
	min-width: 0;
}

.portfolio-section__header-actions {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: flex-end;
	gap: 8px;
	flex: 1 1 auto;
	min-width: 0;
}

.portfolio-header-btn {
	display: inline-block;
	padding: 7px 12px;
	border-radius: 18px;
	font-size: 0.75rem;
	font-weight: 600;
	line-height: 1.2;
	text-transform: lowercase;
	cursor: pointer;
	white-space: nowrap;
	border: none;

	&--outline {
		background: $profile-green;
		color: var(--TU__BtnTxtClrPri, #fff);
	}

	&--primary {
		background: $profile-green;
		color: var(--TU__BtnTxtClrPri, #fff);
	}

	&:disabled {
		opacity: 0.6;
		cursor: default;
	}

	&:active:not(:disabled) {
		background: $profile-green-hover;
	}
}

.portfolio-section__body {
	display: block;
	width: 100%;
	height: auto;
	max-height: none;
	overflow: visible;
	padding: 12px 16px 16px;
	box-sizing: border-box;
}

.portfolio-upload-status {
	font-size: 0.85rem;
	color: $profile-text-muted;
	padding: 8px 0;
}

.resume-list__hint {
	margin: 0 0 10px;
	font-size: 0.8rem;
	color: $profile-text-muted;
	line-height: 1.35;
}

.resume-list {
	display: block;
	width: 100%;
	height: auto;
	max-height: none;
	overflow: visible;
	margin: 0;
	padding: 0;
	list-style: none;
}

.resume-list__item {
	display: block;
	width: 100%;
	height: auto;
	margin: 0 0 8px;
	padding: 0;
	border: 1px solid $profile-border;
	border-radius: 8px;
	background: #fafafa;
	box-sizing: border-box;

	&:last-child {
		margin-bottom: 0;
	}
}

.resume-list__row {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	padding: 10px 12px;
	width: 100%;
	box-sizing: border-box;
}

.resume-list__radio {
	flex-shrink: 0;
	margin: 6px 0 0;
	cursor: pointer;
}

.resume-list__icon {
	flex-shrink: 0;
	font-size: 1.25rem;
	color: #b0b0b0;
	margin-top: 2px;
}

.resume-list__details {
	flex: 1;
	min-width: 0;
	overflow: hidden;
}

.resume-list__filename {
	display: block;
	margin: 0;
	font-size: 0.85rem;
	font-weight: 600;
	color: $profile-text;
	word-break: break-word;
	line-height: 1.35;
	cursor: pointer;
}

.resume-list__meta {
	display: flex;
	flex-direction: column;
	gap: 2px;
	margin-top: 4px;
	font-size: 0.75rem;
	color: $profile-text-muted;
	line-height: 1.3;
}

.resume-list__actions {
	flex-shrink: 0;
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 4px;
}

.resume-list__badge {
	font-size: 0.7rem;
	font-weight: 600;
	color: $profile-green;
	white-space: nowrap;
}

.resume-list__action {
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	border: none;
	background: transparent;
	cursor: pointer;

	.fa-download,
	.fa-trash {
		font-size: 1rem;
		color: $profile-green;
	}
}

.resume-list__empty {
	margin: 0;
	padding: 8px 0;
	font-size: 0.85rem;
	color: $profile-text-muted;
	font-style: italic;
}

.hidden-container {
	visibility: hidden;
	height: 0;
	width: 0;
	overflow: hidden;
	position: absolute;
}
</style>

<style lang="scss">
section.learner_profile_resume.portfolio-section {
	height: auto !important;
	max-height: none !important;
	min-height: 0 !important;
	overflow: visible !important;

	.portfolio-section__body,
	.resume-list,
	.resume-list__item {
		height: auto !important;
		max-height: none !important;
		overflow: visible !important;
	}
}

.bg-profile-popup-header {
	background: #eaf8ff !important;
}
</style>



// WEBPACK FOOTER //
// src/app/dashboard/components/LearnerProfileResume.vue